/**
 * @pj/sim — the authoritative world simulation and the Room seam.
 *
 * `Simulation` is transport-free and DOM-free: it owns world state (multi-map,
 * multi-LEVEL, doors, gates, warps, NPCs, the message/prompt UI lock), runs
 * every level's interactables and objectives, applies effects through a
 * capability context, and advances on fixed ticks. That purity lets the same
 * simulation run inside a LocalRoom now and a Durable Object later.
 *
 * Levels: the whole world is loaded at once — Level 1 (treasures → east gate)
 * and Level 2 ("Bhaav": AANYA, the sudoku computer, the closet easter egg, the
 * 30-minute timer). Crossing a gate is just a warp + `save.currentLevel`;
 * a running Level-2 attempt lives in the save and survives map hops and
 * reloads. Only a timer loss resets it.
 *
 * Field-move fidelity follows Gen 3 (pokeemerald): Surf is contextual — face
 * water from land, press A, confirm, hop on; moving onto land dismounts
 * automatically. Messages lock the field until advanced, like `lockall`.
 *
 * `LocalRoom` runs it in-process on a requestAnimationFrame loop, emitting the
 * same protocol Diffs + RoomEvents a future networked `RemoteRoom` will send.
 */
import {
  DIR_VECTORS,
  STEP_TICKS,
  SURF_STEP_TICKS,
  TICK_HZ,
  TILE_SIZE,
  diffSnapshots,
  emptySnapshot,
  type Command,
  type Diff,
  type Dir,
  type EntityKind,
  type EntitySnap,
  type RoomEvent,
  type WorldSnapshot,
} from "@pj/protocol";
import {
  BHAAV_MAP_ID,
  LEVEL1_ID,
  LEVEL2_ID,
  SURF_PROMPT_LINES,
  SURF_USED_LINES,
  getLevel,
  getMap,
  levelForMap,
  listLevels,
  warpAt,
  type DoorDef,
  type GateDef,
  type LevelDef,
  type MachineDef,
  type MapData,
} from "@pj/content";
import { canEnter, canStartSurf, hashSeed, isLand, type TraversalCtx } from "@pj/rules";
import {
  addExp,
  expForNext,
  giveItem,
  grantAbility,
  hasAbility,
  hasItem,
  isLevelDone,
  isTreasureCollected,
  markLevelDone,
  markTreasureCollected,
  setFlag,
  type SaveState,
} from "@pj/state";
import { runEffects, type Effect, type EffectContext } from "@pj/script";
import { entriesValid, generateSudoku, isSolved, type SudokuPuzzle } from "@pj/sudoku";

/** Level-2 attempt length: 30 minutes of wall-clock time. */
export const BHAAV_TIMER_MS = 30 * 60 * 1000;
/** AANYA spots poopie within this range (Manhattan tiles). */
const SIGHT_RANGE = 6;
/** The force field around crying AANYA: tiles this close are unreachable
 *  until the sudoku is solved (or her TOP is in hand). */
const FORCE_FIELD_RANGE = 1;

interface Entity {
  id: string;
  kind: EntityKind;
  /** NPCs live on a fixed map; the player roams (undefined). */
  mapId?: string;
  sprite: string;
  tx: number;
  ty: number;
  px: number;
  py: number;
  facing: Dir;
  moving: boolean;
  targetTx: number;
  targetTy: number;
  desiredDir: Dir | null;
  surfing: boolean;
}

interface Interactable {
  id: string;
  kind: "treasure" | "sign";
  levelId: string;
  mapId: string;
  tx: number;
  ty: number;
  sprite: string;
  effects: Effect[];
  collected: boolean;
}

type AanyaPhase = "waiting" | "approaching" | "crying" | "leaving" | "gone";

type UiState =
  | { kind: "idle" }
  | { kind: "message"; boxes: string[][]; index: number; then: (() => void) | null }
  | { kind: "prompt"; onReply: (accept: boolean) => void }
  /** A scripted beat is playing (AANYA walking over) — field locked, no box. */
  | { kind: "cutscene" }
  /** The sudoku overlay is open — field locked, commands flow via sudoku*. */
  | { kind: "minigame" };

function approach(cur: number, goal: number, step: number): number {
  if (cur < goal) return Math.min(cur + step, goal);
  if (cur > goal) return Math.max(cur - step, goal);
  return cur;
}

function manhattan(a: { tx: number; ty: number }, b: { tx: number; ty: number }): number {
  return Math.abs(a.tx - b.tx) + Math.abs(a.ty - b.ty);
}

export interface SimulationOptions {
  /** Wall clock, injectable for tests. */
  now?: (() => number) | undefined;
  /** Override the Level-2 timer length (dev/test only). */
  bhaavTimerMs?: number | undefined;
}

export class Simulation {
  tick = 0;
  private currentMap: MapData;
  private readonly save: SaveState;
  private readonly dateKey: string;
  private readonly now: () => number;
  private readonly bhaavTimerMs: number;
  private readonly entities = new Map<string, Entity>();
  private readonly interactables = new Map<string, Interactable>();
  private readonly machines = new Map<string, MachineDef>();
  private readonly doors = new Map<string, DoorDef>();
  private readonly gates = new Map<string, GateDef>();
  /** Open doors AND gates — the single traversal verdict set. */
  private readonly openPortals = new Set<string>();
  private readonly events: RoomEvent[] = [];
  private ui: UiState = { kind: "idle" };
  /** Door/gate whose locked text already fired for the current held direction. */
  private lastBumpPortal: string | null = null;
  private dirty = false;
  /** Treasures collected per level this session. */
  private readonly collectedByLevel = new Map<string, number>();
  private readonly treasureTotals = new Map<string, number>();
  private playerId = "";
  // --- Level 2 (Bhaav) runtime ---
  private aanyaPhase: AanyaPhase = "waiting";
  private puzzleCache: { seed: number; puzzle: SudokuPuzzle } | null = null;

  constructor(save: SaveState, dateKey: string, opts: SimulationOptions = {}) {
    this.save = save;
    this.dateKey = dateKey;
    this.now = opts.now ?? (() => Date.now());
    this.bhaavTimerMs = opts.bhaavTimerMs ?? BHAAV_TIMER_MS;
    if (!listLevels().some((l) => l.id === save.currentLevel)) save.currentLevel = LEVEL1_ID;
    this.currentMap = getMap(getLevel(save.currentLevel).mapId);

    for (const level of listLevels()) {
      this.treasureTotals.set(level.id, level.treasures.length);
      this.collectedByLevel.set(level.id, 0);
      for (const t of level.treasures) {
        const collected = isTreasureCollected(save, level.id, t.id);
        if (collected)
          this.collectedByLevel.set(level.id, (this.collectedByLevel.get(level.id) ?? 0) + 1);
        this.interactables.set(t.id, {
          id: t.id,
          kind: "treasure",
          levelId: level.id,
          mapId: t.mapId,
          tx: t.tx,
          ty: t.ty,
          sprite: t.sprite,
          effects: t.effects,
          collected,
        });
      }
      for (const s of level.signs) {
        this.interactables.set(s.id, {
          id: s.id,
          kind: "sign",
          levelId: level.id,
          mapId: s.mapId,
          tx: s.tx,
          ty: s.ty,
          sprite: "sign",
          effects: s.effects,
          collected: false,
        });
      }
      for (const m of level.machines) this.machines.set(m.id, m);
      for (const d of level.doors) {
        this.doors.set(d.id, d);
        // Keyless doors start open; unlocked-with-an-item doors stay open.
        if (!d.requiresItem || save.openDoors.includes(d.id)) this.openPortals.add(d.id);
      }
      for (const g of level.gates) {
        this.gates.set(g.id, g);
        // Back-gates (to an earlier level) are always open; exit gates of an
        // already-completed level re-open (gates with no destination never do).
        if (g.dest) {
          const destNum = getLevel(g.dest.levelId).num;
          if (destNum < level.num) this.openPortals.add(g.id);
          else if (isLevelDone(this.save, level.id)) this.openPortals.add(g.id);
        }
      }
      for (const n of level.npcs) {
        if (n.id === "aanya" && isLevelDone(this.save, LEVEL2_ID)) continue; // she moved on
        this.entities.set(n.id, {
          id: n.id,
          kind: "npc",
          mapId: n.mapId,
          sprite: n.sprite,
          tx: n.tx,
          ty: n.ty,
          px: n.tx * TILE_SIZE,
          py: n.ty * TILE_SIZE,
          facing: "down",
          moving: false,
          targetTx: n.tx,
          targetTy: n.ty,
          desiredDir: null,
          surfing: false,
        });
      }
    }
    if (isLevelDone(this.save, LEVEL2_ID)) this.aanyaPhase = "gone";
    else if (this.save.bhaav) this.aanyaPhase = "crying"; // resumed attempt
  }

  get map(): MapData {
    return this.currentMap;
  }

  get level(): LevelDef {
    return levelForMap(this.currentMap.id);
  }

  get uiBusy(): boolean {
    return this.ui.kind !== "idle";
  }

  hasItem(item: string): boolean {
    return hasItem(this.save, item);
  }

  isDoorOpen(id: string): boolean {
    return this.openPortals.has(id);
  }

  addPlayer(id: string): void {
    this.playerId = id;
    // Restore the exact standing position if the save has a sane one
    // (on land, on a map of the current level); else the level's spawn.
    let { tx, ty } = this.currentMap.spawn;
    let facing: Dir = "down";
    const pos = this.save.pos;
    if (pos) {
      try {
        const map = getMap(pos.mapId);
        if (levelForMap(map.id).id === this.save.currentLevel && isLand(map, pos.tx, pos.ty)) {
          this.currentMap = map;
          tx = pos.tx;
          ty = pos.ty;
          facing = pos.facing;
        }
      } catch {
        // unknown map in the save — fall back to the level spawn
      }
    }
    this.entities.set(id, {
      id,
      kind: "player",
      sprite: "player",
      tx,
      ty,
      px: tx * TILE_SIZE,
      py: ty * TILE_SIZE,
      facing,
      moving: false,
      targetTx: tx,
      targetTy: ty,
      desiredDir: null,
      surfing: false,
    });
    // Seed the HUD with current progression + objective + timer state.
    this.emitProgress();
    this.emitObjective();
    this.events.push({ type: "ability", ability: "surf", active: false });
    this.emitTimer();
  }

  private player(): Entity | null {
    return this.entities.get(this.playerId) ?? null;
  }

  setDesiredDir(id: string, dir: Dir | null): void {
    const e = this.entities.get(id);
    if (!e) return;
    if (e.desiredDir !== dir) this.lastBumpPortal = null; // re-arm bump text
    e.desiredDir = dir;
  }

  /** The A button: advance an open message, or interact with the faced tile. */
  interact(id: string): void {
    if (this.ui.kind === "message") {
      this.advanceMessage();
      return;
    }
    if (this.ui.kind !== "idle") return; // prompt/cutscene/minigame handle their own input
    const e = this.entities.get(id);
    if (!e || e.moving) return;

    const v = DIR_VECTORS[e.facing];
    const ftx = e.tx + v.dx;
    const fty = e.ty + v.dy;

    // Priority mirrors Gen 3: scripted objects first, then water (surf prompt).
    const npc = this.npcAt(ftx, fty);
    if (npc) {
      this.interactNpc(npc, e);
      return;
    }
    const door = this.doorAt(ftx, fty);
    if (door) {
      this.interactDoor(door);
      return;
    }
    const gate = this.gateAt(ftx, fty);
    if (gate && !this.openPortals.has(gate.id)) {
      this.showGateClosed(gate);
      return;
    }
    const machine = this.machineAt(ftx, fty);
    if (machine) {
      this.interactMachine(machine);
      return;
    }
    const target = this.findInteractable(ftx, fty) ?? this.findInteractable(e.tx, e.ty);
    if (target) {
      const boxes = this.runBatched(target.effects);
      if (target.kind === "treasure") this.collectTreasure(target, boxes);
      this.showBoxes(boxes, null);
      this.emitProgress();
      return;
    }
    this.maybePromptSurf(e, ftx, fty);
  }

  promptReply(accept: boolean): void {
    if (this.ui.kind !== "prompt") return;
    const { onReply } = this.ui;
    this.ui = { kind: "idle" };
    onReply(accept);
    if (this.ui.kind === "idle") this.events.push({ type: "uiClose" });
  }

  // --- doors / gates ----------------------------------------------------------

  private doorAt(tx: number, ty: number): DoorDef | null {
    for (const d of this.doors.values()) {
      if (d.mapId === this.currentMap.id && d.tx === tx && d.ty === ty) return d;
    }
    return null;
  }

  private gateAt(tx: number, ty: number): GateDef | null {
    for (const g of this.gates.values()) {
      if (g.mapId === this.currentMap.id && g.tx === tx && g.ty === ty) return g;
    }
    return null;
  }

  private interactDoor(door: DoorDef): void {
    if (this.openPortals.has(door.id)) {
      this.showBoxes([["The door is open."]], null);
      return;
    }
    if (door.requiresItem && this.hasItem(door.requiresItem)) {
      const boxes = this.runBatched(door.unlockEffects);
      this.showBoxes(boxes, () => {
        this.openPortals.add(door.id);
        if (!this.save.openDoors.includes(door.id)) {
          this.save.openDoors.push(door.id); // stays unlocked forever
          this.dirty = true;
        }
        this.lastBumpPortal = null;
      });
    } else {
      this.showBoxes(this.runBatched(door.lockedEffects), null);
    }
  }

  /** Walking into a locked door reads its locked text once per held press. */
  private bumpDoor(door: DoorDef): void {
    if (this.openPortals.has(door.id)) return;
    if (this.lastBumpPortal === door.id) return;
    this.lastBumpPortal = door.id;
    if (door.requiresItem && this.hasItem(door.requiresItem)) {
      this.interactDoor(door); // bumping with the key in hand unlocks too
      return;
    }
    this.showBoxes(this.runBatched(door.lockedEffects), null);
  }

  private bumpGate(gate: GateDef): void {
    if (this.openPortals.has(gate.id)) return;
    if (this.lastBumpPortal === gate.id) return;
    this.lastBumpPortal = gate.id;
    this.showGateClosed(gate);
  }

  private showGateClosed(gate: GateDef): void {
    // The LEVEL-2 exit gate speaks for the story state.
    if (gate.id === "gate_level2" && isLevelDone(this.save, LEVEL2_ID)) {
      this.showBoxes(
        [["AANYA went on ahead."], ["Come back tomorrow for", "the next level!"]],
        null,
      );
      return;
    }
    this.showBoxes(this.runBatched(gate.closedEffects), null);
  }

  // --- surf ------------------------------------------------------------------

  private maybePromptSurf(e: Entity, ftx: number, fty: number): void {
    // Faithful to Gen 3: without the ability, facing water does nothing at all.
    if (!hasAbility(this.save, "surf")) return;
    if (!canStartSurf(this.currentMap, e.tx, e.ty, e.facing, this.traversalCtx(e))) return;
    if (this.findInteractable(ftx, fty)) return; // occupied water tile: no prompt
    this.ui = {
      kind: "prompt",
      onReply: (accept) => {
        if (!accept) return;
        this.showBoxes([[...SURF_USED_LINES]], () => this.mountSurf(e, ftx, fty));
      },
    };
    this.events.push({ type: "prompt", lines: [...SURF_PROMPT_LINES] });
  }

  private mountSurf(e: Entity, ftx: number, fty: number): void {
    e.surfing = true;
    e.targetTx = ftx;
    e.targetTy = fty;
    e.moving = true; // the one-tile hop onto the water
    this.events.push({ type: "ability", ability: "surf", active: true });
  }

  private dismountSurf(e: Entity): void {
    e.surfing = false;
    this.events.push({ type: "ability", ability: "surf", active: false });
  }

  // --- interactables / effects -------------------------------------------------

  private findInteractable(tx: number, ty: number): Interactable | null {
    for (const it of this.interactables.values()) {
      if (!it.collected && it.mapId === this.currentMap.id && it.tx === tx && it.ty === ty)
        return it;
    }
    return null;
  }

  private machineAt(tx: number, ty: number): MachineDef | null {
    for (const m of this.machines.values()) {
      if (m.mapId === this.currentMap.id && m.tx === tx && m.ty === ty) return m;
    }
    return null;
  }

  private npcAt(tx: number, ty: number): Entity | null {
    for (const e of this.entities.values()) {
      if (e.kind !== "npc" || e.mapId !== this.currentMap.id) continue;
      if ((e.tx === tx && e.ty === ty) || (e.moving && e.targetTx === tx && e.targetTy === ty))
        return e;
    }
    return null;
  }

  private collectTreasure(it: Interactable, boxes: string[][]): void {
    it.collected = true;
    markTreasureCollected(this.save, it.levelId, it.id); // never respawns
    this.dirty = true;
    const count = (this.collectedByLevel.get(it.levelId) ?? 0) + 1;
    this.collectedByLevel.set(it.levelId, count);
    this.emitObjective();
    const total = this.treasureTotals.get(it.levelId) ?? 0;
    if (count >= total && total > 0) {
      const level = getLevel(it.levelId);
      boxes.push(...this.runBatched(level.reward));
      // Open this level's exit gates (the ones leading forward).
      for (const g of level.gates) {
        if (g.dest && getLevel(g.dest.levelId).num > level.num) this.openPortals.add(g.id);
      }
      if (!isLevelDone(this.save, level.id)) {
        markLevelDone(this.save, level.id, this.dateKey);
        this.dirty = true;
      }
      this.emitObjective();
    }
  }

  /** Run effects, returning their dialogue boxes for queued display. */
  private runBatched(effects: readonly Effect[]): string[][] {
    const boxes: string[][] = [];
    runEffects(effects, this.effectCtx(boxes));
    return boxes;
  }

  private effectCtx(boxes: string[][]): EffectContext {
    return {
      dialogue: (lines) => boxes.push([...lines]),
      toast: (text) => this.events.push({ type: "toast", text }),
      setFlag: (flag) => {
        setFlag(this.save, flag);
        this.dirty = true;
      },
      grantAbility: (ability) => {
        if (grantAbility(this.save, ability)) this.dirty = true;
      },
      giveItem: (item) => {
        if (giveItem(this.save, item)) this.dirty = true;
      },
      addExp: (amount) => {
        const powered = addExp(this.save, amount);
        this.dirty = true;
        if (powered)
          this.events.push({ type: "toast", text: `Power up! You're now Power ${powered}.` });
      },
    };
  }

  // --- Level 2: Bhaav -----------------------------------------------------------

  private aanya(): Entity | null {
    return this.entities.get("aanya") ?? null;
  }

  private puzzle(): SudokuPuzzle | null {
    const seed = this.save.bhaav?.sudokuSeed;
    if (seed === undefined) return null;
    if (this.puzzleCache?.seed !== seed) {
      this.puzzleCache = { seed, puzzle: generateSudoku(seed) };
    }
    return this.puzzleCache.puzzle;
  }

  /** AANYA spots poopie: the attempt (and its 30-minute clock) begins. */
  private startBhaavAttempt(): void {
    const seed = (Math.floor(this.now()) ^ hashSeed(this.dateKey)) >>> 0;
    const puzzle = generateSudoku(seed);
    this.save.bhaav = {
      deadline: this.now() + this.bhaavTimerMs,
      sudokuSeed: seed,
      entries: puzzle.givens,
      solved: false,
      closetOpened: false,
      hasTop: false,
    };
    this.dirty = true;
    this.aanyaPhase = "approaching";
    this.ui = { kind: "cutscene" };
    this.events.push({ type: "toast", text: "AANYA spotted poopie!" });
    this.emitTimer();
  }

  /** Per-tick Level-2 logic: sight, approach, proximity hint, the timer. */
  private bhaavStep(): void {
    const player = this.player();
    const aanya = this.aanya();
    if (!player) return;

    // The clock only exists while an attempt runs and the level isn't done.
    if (this.save.bhaav && !isLevelDone(this.save, LEVEL2_ID)) {
      if (this.tick % TICK_HZ === 0) this.emitTimer();
      const expired = this.now() >= this.save.bhaav.deadline;
      if (expired && (this.ui.kind === "idle" || this.ui.kind === "minigame")) {
        this.loseBhaavAttempt();
        return;
      }
    }

    if (!aanya || this.aanyaPhase === "gone") return;
    const sameMap = this.currentMap.id === aanya.mapId;

    switch (this.aanyaPhase) {
      case "waiting": {
        if (!sameMap || this.ui.kind !== "idle" || this.save.bhaav) break;
        if (this.currentMap.id !== BHAAV_MAP_ID) break;
        if (manhattan(player, aanya) <= SIGHT_RANGE) this.startBhaavAttempt();
        break;
      }
      case "approaching": {
        if (!sameMap) break; // she pauses if poopie warps away mid-walk
        if (aanya.moving) break;
        if (manhattan(player, aanya) <= 1) {
          this.faceEachOther(aanya, player);
          this.aanyaPhase = "crying";
          this.showBoxes(
            [
              ["AANYA: poopie!! sniff…", "sniff… waaah…"],
              ["AANYA: give me bhaav!!"],
              ["(AANYA is crying.)"],
            ],
            null,
          );
          this.emitObjective();
          break;
        }
        this.stepNpcToward(aanya, player.tx, player.ty, false);
        break;
      }
      case "crying":
        break; // the force field around her is enforced in the movement step
      case "leaving": {
        if (!sameMap || aanya.moving) break;
        const gate = this.gates.get("gate_level2");
        if (!gate) break;
        if (aanya.tx === gate.tx && aanya.ty === gate.ty) {
          this.entities.delete("aanya");
          this.aanyaPhase = "gone";
          break;
        }
        this.stepNpcToward(aanya, gate.tx, gate.ty, true);
        break;
      }
    }
  }

  /** One pathing step toward the target. `intoGate` lets her exit through the
   *  (visually open, traversal-closed) LEVEL-3 gate tile. */
  private stepNpcToward(npc: Entity, tx: number, ty: number, intoGate: boolean): void {
    const dx = tx - npc.tx;
    const dy = ty - npc.ty;
    const tryDirs: Dir[] = [];
    const xDir: Dir | null = dx > 0 ? "right" : dx < 0 ? "left" : null;
    const yDir: Dir | null = dy > 0 ? "down" : dy < 0 ? "up" : null;
    if (Math.abs(dx) >= Math.abs(dy)) {
      if (xDir) tryDirs.push(xDir);
      if (yDir) tryDirs.push(yDir);
    } else {
      if (yDir) tryDirs.push(yDir);
      if (xDir) tryDirs.push(xDir);
    }
    const player = this.player();
    for (const dir of tryDirs) {
      const v = DIR_VECTORS[dir];
      const ntx = npc.tx + v.dx;
      const nty = npc.ty + v.dy;
      if (player && player.tx === ntx && player.ty === nty) continue;
      if (player && player.moving && player.targetTx === ntx && player.targetTy === nty) continue;
      if (this.findInteractable(ntx, nty) || this.machineAt(ntx, nty)) continue;
      const enterable =
        canEnter(this.currentMap, ntx, nty, this.traversalCtx(npc)) ||
        (intoGate && ntx === tx && nty === ty);
      if (!enterable) continue;
      npc.facing = dir;
      npc.targetTx = ntx;
      npc.targetTy = nty;
      npc.moving = true;
      return;
    }
    // Boxed in: wait this tick, face the goal.
    if (tryDirs[0]) npc.facing = tryDirs[0];
  }

  private faceEachOther(npc: Entity, player: Entity): void {
    const dx = npc.tx - player.tx;
    const dy = npc.ty - player.ty;
    if (Math.abs(dx) > Math.abs(dy)) {
      player.facing = dx > 0 ? "right" : "left";
      npc.facing = dx > 0 ? "left" : "right";
    } else {
      player.facing = dy > 0 ? "down" : "up";
      npc.facing = dy > 0 ? "up" : "down";
    }
  }

  /** Crying AANYA is wrapped in a force field: the player bounces off the
   *  tiles next to her until the sudoku is solved (or her TOP is in hand).
   *  Solving dissolves it — then the hug, the real achievement, can happen. */
  private blockedByForceField(e: Entity, ntx: number, nty: number): boolean {
    if (e.kind !== "player") return false;
    const bhaav = this.save.bhaav;
    if (!bhaav || bhaav.solved || bhaav.hasTop) return false;
    if (this.aanyaPhase !== "crying") return false;
    const aanya = this.aanya();
    if (!aanya || aanya.mapId !== this.currentMap.id) return false;
    const cur = manhattan(e, aanya);
    const next = manhattan({ tx: ntx, ty: nty }, aanya);
    if (next > FORCE_FIELD_RANGE || next >= cur) return false; // out/along is fine
    // Bounce + the hint, once per held press (like door bumps).
    if (this.lastBumpPortal !== "force_field") {
      this.lastBumpPortal = "force_field";
      this.showBoxes([["Let me play a game first."]], null);
    }
    return true;
  }

  private interactNpc(npc: Entity, player: Entity): void {
    if (npc.id !== "aanya") return;
    if (this.aanyaPhase === "leaving") return; // she's on her way out
    this.faceEachOther(npc, player);
    const bhaav = this.save.bhaav;
    if (!bhaav || this.aanyaPhase !== "crying") {
      // Shouldn't normally happen (sight starts the attempt first), but cover it.
      this.showBoxes([["AANYA: sniff…"]], null);
      return;
    }
    if (bhaav.solved) {
      this.completeBhaav("hug");
      return;
    }
    if (bhaav.hasTop) {
      this.completeBhaav("top");
      return;
    }
    this.showBoxes(
      [["AANYA: bhaav… give me", "bhaav… sniff…"], ["Let me play a game first."]],
      null,
    );
  }

  private completeBhaav(via: "hug" | "top"): void {
    const boxes: string[][] =
      via === "hug"
        ? [
            ["poopie gave AANYA a", "great big hug!"],
            ["AANYA: …ehehe ♥"],
          ]
        : [
            ["poopie gave AANYA her", "favourite TOP!"],
            ["AANYA: !!! my top!! ♥"],
            ["poopie hugged AANYA too."],
          ];
    boxes.push(...this.runBatched(getLevel(LEVEL2_ID).reward));
    this.events.push({ type: "toast", text: "Achievement unlocked: BHAAV ♥" });
    markLevelDone(this.save, LEVEL2_ID, this.dateKey);
    this.save.bhaav = null;
    this.dirty = true;
    this.emitTimer();
    this.emitObjective();
    this.showBoxes(boxes, () => {
      const aanya = this.aanya();
      if (aanya) {
        aanya.sprite = "aanya"; // no more tears
        this.aanyaPhase = "leaving";
      } else {
        this.aanyaPhase = "gone";
      }
    });
  }

  /** The timer ran out: the attempt resets and poopie is sent back to LEVEL 1. */
  private loseBhaavAttempt(): void {
    if (this.ui.kind === "minigame") {
      this.events.push({ type: "sudokuClose" });
      this.ui = { kind: "idle" };
    }
    this.save.bhaav = null;
    this.dirty = true;
    this.emitTimer();
    // AANYA goes back to her spot, still crying.
    const aanya = this.aanya();
    const def = getLevel(LEVEL2_ID).npcs.find((n) => n.id === "aanya");
    if (aanya && def) {
      aanya.tx = def.tx;
      aanya.ty = def.ty;
      aanya.px = def.tx * TILE_SIZE;
      aanya.py = def.ty * TILE_SIZE;
      aanya.moving = false;
      aanya.facing = "down";
    }
    this.aanyaPhase = "waiting";
    this.showBoxes(
      [
        ["AANYA: …poopie never gave", "me bhaav… WAAAAH!!"],
        ["Time's up!"],
        ["poopie was sent back", "to LEVEL 1…"],
      ],
      () => {
        const player = this.player();
        if (!player) return;
        if (player.surfing) this.dismountSurf(player);
        const level1 = getLevel(LEVEL1_ID);
        this.save.currentLevel = LEVEL1_ID;
        this.dirty = true;
        const map = getMap(level1.mapId);
        this.warpTo(player, map.id, map.spawn.tx, map.spawn.ty, "down");
        this.emitObjective();
      },
    );
  }

  // --- machines (the computer, the closet) ---------------------------------------

  private interactMachine(machine: MachineDef): void {
    if (machine.kind === "computer") this.interactComputer();
    else this.interactCloset();
  }

  private interactComputer(): void {
    if (isLevelDone(this.save, LEVEL2_ID)) {
      this.showBoxes([["The screen shows a solved", "sudoku. AANYA has her bhaav."]], null);
      return;
    }
    const bhaav = this.save.bhaav;
    if (!bhaav) {
      this.showBoxes([["A computer. Its screen", "is dark."], ["…I should find AANYA first."]], null);
      return;
    }
    if (bhaav.solved) {
      this.showBoxes(
        [["Game's done.", "No more excuses, poopie."], ["Time to give AANYA her hug."]],
        null,
      );
      return;
    }
    this.ui = { kind: "minigame" };
    this.events.push({ type: "sudoku", seed: bhaav.sudokuSeed, entries: bhaav.entries });
  }

  private interactCloset(): void {
    if (isLevelDone(this.save, LEVEL2_ID)) {
      this.showBoxes([["A tidy closet."]], null);
      return;
    }
    const bhaav = this.save.bhaav;
    if (!bhaav) {
      this.showBoxes([["A closet. I shouldn't snoop", "around right now."]], null);
      return;
    }
    if (bhaav.hasTop) {
      this.showBoxes([["The closet is tidy.", "(One hanger is empty.)"]], null);
      return;
    }
    if (!bhaav.closetOpened) {
      bhaav.closetOpened = true;
      this.dirty = true;
      this.showBoxes([["poopie opened the closet."]], null);
      return;
    }
    // Second look: the clothes, then a long, guilt-laden road to the take —
    // the easy answer at every step is to walk away.
    this.showBoxes(
      [
        ["There are clothes neatly", "folded inside."],
        ["One of them looks familiar…"],
      ],
      () => {
        this.ui = {
          kind: "prompt",
          onReply: (leaveIt) => {
            if (leaveIt) {
              this.showBoxes([["Yes. Tidy and neat.", "Just how AANYA likes it."]], null);
              return;
            }
            this.showBoxes(
              [
                ["…it's AANYA's favourite TOP."],
                ["If AANYA finds out poopie was", "digging through her closet…"],
                ["…she might get even MORE", "angry."],
                ["The crying would get LOUDER.", "Much, much louder."],
                ["And it's her FAVOURITE.", "Of all the tops. THE one."],
              ],
              () => {
                this.ui = {
                  kind: "prompt",
                  onReply: (take) => {
                    if (!take) {
                      this.showBoxes(
                        [["poopie left the TOP alone.", "(Tidy and neat.)"]],
                        null,
                      );
                      return;
                    }
                    bhaav.hasTop = true;
                    this.dirty = true;
                    this.showBoxes(
                      [["poopie took AANYA's TOP."], ["(It still smells like", "laundry day.)"]],
                      null,
                    );
                  },
                };
                this.events.push({ type: "prompt", lines: ["Take AANYA's TOP anyway?"] });
              },
            );
          },
        };
        this.events.push({ type: "prompt", lines: ["Let's leave it tidy and neat?"] });
      },
    );
  }

  // --- sudoku commands -------------------------------------------------------------

  sudokuEntries(entries: string): void {
    const bhaav = this.save.bhaav;
    const puzzle = this.puzzle();
    if (!bhaav || !puzzle || bhaav.solved) return;
    if (!entriesValid(puzzle, entries)) return;
    bhaav.entries = entries;
    this.dirty = true;
    if (isSolved(puzzle, entries)) {
      bhaav.solved = true;
      if (this.ui.kind === "minigame") {
        this.events.push({ type: "sudokuClose" });
        this.ui = { kind: "idle" };
      }
      this.showBoxes(
        [["Solved it!"], ["…oh. Right. AANYA.", "She's still crying."], ["Okay, okay. Bhaav time."]],
        null,
      );
      this.emitObjective();
    }
  }

  sudokuExit(): void {
    if (this.ui.kind !== "minigame") return;
    this.ui = { kind: "idle" };
    this.events.push({ type: "sudokuClose" });
    this.events.push({ type: "uiClose" });
  }

  // --- message boxes -----------------------------------------------------------

  private showBoxes(boxes: string[][], then: (() => void) | null): void {
    if (boxes.length === 0) {
      then?.();
      return;
    }
    this.ui = { kind: "message", boxes, index: 0, then };
    this.events.push({ type: "message", lines: boxes[0]!, more: boxes.length > 1 });
  }

  private advanceMessage(): void {
    if (this.ui.kind !== "message") return;
    const next = this.ui.index + 1;
    if (next < this.ui.boxes.length) {
      this.ui.index = next;
      this.events.push({
        type: "message",
        lines: this.ui.boxes[next]!,
        more: next < this.ui.boxes.length - 1,
      });
      return;
    }
    const { then } = this.ui;
    this.ui = { kind: "idle" };
    this.events.push({ type: "uiClose" });
    then?.();
  }

  // --- HUD events ----------------------------------------------------------------

  private emitProgress(): void {
    this.events.push({
      type: "progress",
      exp: this.save.exp,
      power: this.save.power,
      expForNext: expForNext(this.save),
      abilities: [...this.save.abilities],
    });
  }

  private emitObjective(): void {
    const level = this.level;
    if (level.id === LEVEL2_ID) {
      const done = isLevelDone(this.save, LEVEL2_ID);
      const bhaav = this.save.bhaav;
      const step = done ? 3 : !bhaav ? 0 : bhaav.solved || bhaav.hasTop ? 2 : 1;
      const label = done
        ? "Bhaav delivered"
        : !bhaav
          ? "Find AANYA"
          : bhaav.solved || bhaav.hasTop
            ? "Give AANYA her bhaav"
            : "Game first, bhaav later"; // poopie's priorities, not ours
      this.events.push({
        type: "objective",
        levelNum: level.num,
        levelName: level.name,
        label,
        progress: step,
        total: 3,
        complete: done,
      });
      return;
    }
    const total = this.treasureTotals.get(level.id) ?? 0;
    const progress = this.collectedByLevel.get(level.id) ?? 0;
    this.events.push({
      type: "objective",
      levelNum: level.num,
      levelName: level.name,
      label: level.objectiveLabel,
      progress,
      total,
      complete: isLevelDone(this.save, level.id) || (total > 0 && progress >= total),
    });
  }

  private emitTimer(): void {
    const bhaav = this.save.bhaav;
    if (bhaav && !isLevelDone(this.save, LEVEL2_ID)) {
      this.events.push({ type: "timer", remainingMs: Math.max(0, bhaav.deadline - this.now()) });
    } else {
      this.events.push({ type: "timer", remainingMs: null });
    }
  }

  // --- movement / warps ----------------------------------------------------------

  private traversalCtx(e: Entity): TraversalCtx {
    return {
      surfing: e.surfing,
      openDoors: this.openPortals,
      doorIdAt: (tx, ty) => this.doorAt(tx, ty)?.id ?? this.gateAt(tx, ty)?.id ?? null,
    };
  }

  private warpTo(e: Entity, mapId: string, tx: number, ty: number, facing: Dir): void {
    this.currentMap = getMap(mapId);
    e.tx = tx;
    e.ty = ty;
    e.targetTx = tx;
    e.targetTy = ty;
    e.px = tx * TILE_SIZE;
    e.py = ty * TILE_SIZE;
    e.facing = facing;
    e.moving = false;
    this.savePos(e);
  }

  /** Remember where poopie stands (restored on the next load). */
  private savePos(e: Entity): void {
    if (e.kind !== "player") return;
    const pos = this.save.pos;
    if (pos && pos.mapId === this.currentMap.id && pos.tx === e.tx && pos.ty === e.ty) return;
    this.save.pos = { mapId: this.currentMap.id, tx: e.tx, ty: e.ty, facing: e.facing };
    this.dirty = true;
  }

  step(): void {
    this.tick++;
    for (const e of this.entities.values()) {
      if (e.kind !== "player" && e.kind !== "npc") continue;
      if (e.kind === "npc" && e.mapId !== this.currentMap.id) continue; // off-screen NPCs pause
      const speed = TILE_SIZE / (e.surfing ? SURF_STEP_TICKS : STEP_TICKS);
      if (e.moving) {
        const goalX = e.targetTx * TILE_SIZE;
        const goalY = e.targetTy * TILE_SIZE;
        e.px = approach(e.px, goalX, speed);
        e.py = approach(e.py, goalY, speed);
        if (e.px === goalX && e.py === goalY) {
          e.tx = e.targetTx;
          e.ty = e.targetTy;
          e.moving = false;
          if (e.kind === "player") this.onArrive(e);
          // Rest one tick on arrival so a released key actually stops the entity
          // on the tile it reached (instead of re-stepping the same tick).
          continue;
        }
      }
      // Messages and prompts lock the field, Gen-3 `lockall` style.
      if (e.kind === "player" && !e.moving && e.desiredDir && !this.uiBusy) {
        e.facing = e.desiredDir;
        const v = DIR_VECTORS[e.desiredDir];
        const ntx = e.tx + v.dx;
        const nty = e.ty + v.dy;
        const door = this.doorAt(ntx, nty);
        if (door && !this.openPortals.has(door.id)) {
          this.bumpDoor(door);
          continue;
        }
        const gate = this.gateAt(ntx, nty);
        if (gate && !this.openPortals.has(gate.id)) {
          this.bumpGate(gate);
          continue;
        }
        if (this.findInteractable(ntx, nty)) continue; // items/signs are solid
        if (this.machineAt(ntx, nty)) continue; //        machines are solid
        if (this.npcAt(ntx, nty)) continue; //            NPCs are solid
        if (this.blockedByForceField(e, ntx, nty)) continue;
        if (canEnter(this.currentMap, ntx, nty, this.traversalCtx(e))) {
          e.targetTx = ntx;
          e.targetTy = nty;
          e.moving = true;
        }
      }
    }
    this.bhaavStep();
  }

  /** Tile-arrival triggers: surf dismount, door/gate warps, step-on warps. */
  private onArrive(e: Entity): void {
    if (e.surfing && isLand(this.currentMap, e.tx, e.ty)) this.dismountSurf(e);
    const door = this.doorAt(e.tx, e.ty);
    if (door && this.openPortals.has(door.id)) {
      this.warpTo(e, door.warp.mapId, door.warp.tx, door.warp.ty, door.warp.facing);
      return;
    }
    const gate = this.gateAt(e.tx, e.ty);
    if (gate && this.openPortals.has(gate.id) && gate.dest) {
      this.warpTo(e, gate.dest.mapId, gate.dest.tx, gate.dest.ty, gate.dest.facing);
      if (this.save.currentLevel !== gate.dest.levelId) {
        this.save.currentLevel = gate.dest.levelId;
        this.dirty = true;
      }
      this.emitObjective();
      return;
    }
    const warp = warpAt(this.currentMap, e.tx, e.ty);
    if (warp) {
      this.warpTo(e, warp.destMapId, warp.destTx, warp.destTy, warp.facing);
      return;
    }
    this.savePos(e); // plain step: remember the tile
  }

  // --- snapshots ------------------------------------------------------------------

  snapshot(): WorldSnapshot {
    const entities: Record<string, EntitySnap> = {};
    for (const e of this.entities.values()) {
      if (e.kind === "npc" && e.mapId !== this.currentMap.id) continue;
      const sprite =
        e.id === "aanya"
          ? this.aanyaPhase === "leaving" || this.aanyaPhase === "gone"
            ? "aanya"
            : "aanya_cry"
          : e.sprite;
      entities[e.id] = {
        id: e.id,
        kind: e.kind,
        tx: e.tx,
        ty: e.ty,
        px: Math.round(e.px),
        py: Math.round(e.py),
        facing: e.facing,
        moving: e.moving,
        surfing: e.surfing,
        sprite,
      };
    }
    for (const it of this.interactables.values()) {
      if (it.collected || it.mapId !== this.currentMap.id) continue;
      entities[it.id] = {
        id: it.id,
        kind: it.kind,
        tx: it.tx,
        ty: it.ty,
        px: it.tx * TILE_SIZE,
        py: it.ty * TILE_SIZE,
        facing: "down",
        moving: false,
        surfing: false,
        sprite: it.sprite,
      };
    }
    for (const m of this.machines.values()) {
      if (m.mapId !== this.currentMap.id) continue;
      const sprite =
        m.kind === "closet" && this.save.bhaav?.closetOpened ? "closet_open" : m.sprite;
      entities[m.id] = {
        id: m.id,
        kind: "sign",
        tx: m.tx,
        ty: m.ty,
        px: m.tx * TILE_SIZE,
        py: m.ty * TILE_SIZE,
        facing: "down",
        moving: false,
        surfing: false,
        sprite,
      };
    }
    for (const d of this.doors.values()) {
      if (d.mapId !== this.currentMap.id) continue;
      entities[d.id] = {
        id: d.id,
        kind: "door",
        tx: d.tx,
        ty: d.ty,
        px: d.tx * TILE_SIZE,
        py: d.ty * TILE_SIZE,
        facing: "down",
        moving: false,
        surfing: false,
        sprite: this.openPortals.has(d.id) ? "door_open" : "door_closed",
      };
    }
    for (const g of this.gates.values()) {
      if (g.mapId !== this.currentMap.id) continue;
      const open =
        this.openPortals.has(g.id) || (g.id === "gate_level2" && this.aanyaPhase === "leaving");
      entities[g.id] = {
        id: g.id,
        kind: "door",
        tx: g.tx,
        ty: g.ty,
        px: g.tx * TILE_SIZE,
        py: g.ty * TILE_SIZE,
        facing: "down",
        moving: false,
        surfing: false,
        sprite: open ? "gate_open" : "gate_closed",
      };
    }
    return { tick: this.tick, mapId: this.currentMap.id, entities };
  }

  drainEvents(): RoomEvent[] {
    if (this.events.length === 0) return [];
    return this.events.splice(0, this.events.length);
  }

  takeDirty(): boolean {
    if (!this.dirty) return false;
    this.dirty = false;
    return true;
  }
}

export interface Room {
  readonly mode: string;
  join(name: string): { actorId: string; snapshot: WorldSnapshot };
  send(cmd: Command): void;
  onDiff(cb: (d: Diff) => void): () => void;
  onEvent(cb: (e: RoomEvent) => void): () => void;
  start(): void;
  stop(): void;
}

export interface LocalRoomOptions {
  save: SaveState;
  dateKey: string;
  /** Wall clock, injectable for tests. */
  now?: (() => number) | undefined;
  /** Override the Level-2 timer length (dev/test only). */
  bhaavTimerMs?: number | undefined;
  /** Called with the serialized save whenever progression changes. */
  onPersist?: (() => void) | undefined;
}

const MAX_CATCHUP_STEPS = 5;

export class LocalRoom implements Room {
  readonly mode = "overworld";
  private readonly sim: Simulation;
  private readonly actorId = "player-1";
  private readonly onPersist: (() => void) | undefined;
  private prev: WorldSnapshot;
  private readonly diffCbs = new Set<(d: Diff) => void>();
  private readonly eventCbs = new Set<(e: RoomEvent) => void>();
  private rafId = 0;
  private lastTime = 0;
  private acc = 0;

  constructor(opts: LocalRoomOptions) {
    this.sim = new Simulation(opts.save, opts.dateKey, {
      now: opts.now,
      bhaavTimerMs: opts.bhaavTimerMs,
    });
    this.prev = emptySnapshot(this.sim.map.id);
    this.onPersist = opts.onPersist;
  }

  get localActorId(): string {
    return this.actorId;
  }

  get level(): LevelDef {
    return this.sim.level;
  }

  get mapId(): string {
    return this.sim.map.id;
  }

  join(name: string): { actorId: string; snapshot: WorldSnapshot } {
    void name;
    this.sim.addPlayer(this.actorId);
    const snapshot = this.sim.snapshot();
    this.prev = snapshot;
    this.events.push({ type: "joined", actorId: this.actorId });
    queueMicrotask(() => this.flush());
    return { actorId: this.actorId, snapshot };
  }

  private readonly events: RoomEvent[] = [];

  send(cmd: Command): void {
    switch (cmd.type) {
      case "intent":
        this.sim.setDesiredDir(this.actorId, cmd.dir);
        break;
      case "interact":
        this.sim.interact(this.actorId);
        break;
      case "promptReply":
        this.sim.promptReply(cmd.accept);
        break;
      case "sudokuEntries":
        this.sim.sudokuEntries(cmd.entries);
        break;
      case "sudokuExit":
        this.sim.sudokuExit();
        break;
      case "join":
        break;
    }
    this.flush();
  }

  onDiff(cb: (d: Diff) => void): () => void {
    this.diffCbs.add(cb);
    return () => this.diffCbs.delete(cb);
  }

  onEvent(cb: (e: RoomEvent) => void): () => void {
    this.eventCbs.add(cb);
    return () => this.eventCbs.delete(cb);
  }

  private flush(): void {
    const queued = this.events.splice(0, this.events.length);
    for (const e of queued) this.emitEvent(e);
    for (const e of this.sim.drainEvents()) this.emitEvent(e);
    if (this.sim.takeDirty()) this.onPersist?.();
  }

  private emitDiff(d: Diff): void {
    for (const cb of this.diffCbs) cb(d);
  }

  private emitEvent(e: RoomEvent): void {
    for (const cb of this.eventCbs) cb(e);
  }

  start(): void {
    if (this.rafId) return;
    const stepMs = 1000 / TICK_HZ;
    const loop = (now: number): void => {
      if (!this.lastTime) this.lastTime = now;
      this.acc += now - this.lastTime;
      this.lastTime = now;
      let steps = 0;
      while (this.acc >= stepMs && steps < MAX_CATCHUP_STEPS) {
        this.sim.step();
        this.acc -= stepMs;
        steps++;
      }
      if (this.acc > stepMs * MAX_CATCHUP_STEPS) this.acc = 0;
      const next = this.sim.snapshot();
      const d = diffSnapshots(this.prev, next);
      if (d.upsert.length || d.remove.length) {
        this.emitDiff(d);
        this.prev = next;
      }
      this.flush();
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.lastTime = 0;
    this.acc = 0;
  }
}
